import { computed, ref, onBeforeUnmount, Ref, inject, provide } from '@vue/composition-api';
import ConcurrentManager from '@berlin-front/common/ConcurrentManager';
import { NonStrict, ZodUnwrap, deferred, zodErrorToMessage, zodUnwrap } from '@berlin-front/common/utils';

import { callbackResponseDataSchema } from '@berlin-front/common/schemas/callback-base-schema';

import { z } from 'zod';

/*
モーダルの起動シーケンス

1. [会員サイトdomain] - messageChannel messagePortを生成。
2. [会員サイトdomain] - messagePortに初期化メッセージを送信。(1以降ならどのタイミングでも良い。channel生成と同期的に実行できる。)
3. [会員サイトdomain] - モーダルのURLを指定したiframeを生成。
4. [会員サイトdomain] - iframeに`'load'`イベントハンドラを仕掛ける。
5. [会員サイトdomain] - iframeを画面に配置。
6. [iframe(モーダル)] - iframe内にモーダルのhtmlがロードされ、jsのプログラムが走る。
7. [iframe(モーダル)] - 初期メッセージ受け取り準備が完了する。
8. [会員サイトdomain] - iframeで`'load'`イベントが発火
9. [会員サイトdomain] - messagePortをモーダルに転送する。

modalと会員サイト間の通信は基本的にmessagePortを使う。window.postMessageは無差別盗聴と不正メッセージの送信に晒されがち。
旧atoneではangular loadedのようなイベントをモーダル側から送信しているが、iframeのloadイベントを捕捉するのが一般的実装として正しい。

- iframeにイベントハンドラを仕掛けてから配置した場合、仕掛けたイベントハンドラは一番最初に実行されることが保証されるため、不正なメッセージを割り込ませられる心配がなくなる。
- 最初のメッセージでmessagePortを渡した後はwindow.postMessageを使用しないため、配置済みのモーダルに対する攻撃はほぼできないと思って良い。

*/

/**
 * modal -> クライアントへのコールバック呼び出し用ポート。
 *
 * コールバックの実行完了を検出できるよう、コールバックの呼び出しは必ずresponseを受け取る仕組みになってる。
 */
let clientCallbackPort: MessagePort | undefined;

/**
 * クライアント側からのコマンド受け付けポート。
 *
 * コマンド受付の完了は返さない。完全に一方通行。
 * クライアント -> モーダルへのサービス開始リクエストは発生したタイミングで同期的に画面をロックする必要があるため、
 */
let commandReceiverPort: MessagePort | undefined;

/**
 * モーダルから会員サイトへのコールバック実行要求はシリアルで送信する。
 *
 * 多分その方が会員サイト側のバグ減るかなって配慮だけど、外せばパラレルで飛びます。
 */
const concurrentManager = new ConcurrentManager();

const [connectionCompletePromise, connectionResolve] = deferred<void>();

let currentIssueNumber = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

/**
 * 連番を発番する。
 *
 * ただし開始値はランダムに決定される。
 * @returns
 */
const issueNumber = () => {
  currentIssueNumber =
    currentIssueNumber === Number.MAX_SAFE_INTEGER ? Number.MIN_SAFE_INTEGER : currentIssueNumber + 1;
  return currentIssueNumber;
};

type SchemaDefinition = {
  service?: { service_type: string; modal_mode: string };
  classifier?: z.ZodTypeAny;
  schema: z.ZodObject<
    Record<
      string,
      | z.ZodFunction<z.ZodTuple<any, any>, z.ZodTypeAny> // eslint-disable-line @typescript-eslint/no-explicit-any
      | z.ZodOptional<z.ZodFunction<z.ZodTuple<any, any>, z.ZodTypeAny>> // eslint-disable-line @typescript-eslint/no-explicit-any
      | z.ZodDefault<z.ZodFunction<z.ZodTuple<any, any>, z.ZodTypeAny>> // eslint-disable-line @typescript-eslint/no-explicit-any
    >
  >;
};

const useClientConnection = () => {
  let unmounted = false;
  let unlistenList: (() => void)[] = [];
  onBeforeUnmount(() => {
    unmounted = true;
    unlistenList.splice(0).forEach((ul) => ul());
  });

  const con = {
    /**
     * クライアントコールバックを実行します。
     *
     * @param callbackSchemaDefinition コールバックのみからなるZodスキーマ定義
     * @param callback 上記すキーマ定義に含まれるコールバック名
     * @param params コールバックに渡すパラメータ名をタプル形式で。タプルの第一要素が第一引数で、第二要素が第二引数で・・・・・と、引数の分だけ要素を持つタプルを指定する。
     * @throws コールバックが応答しない時、もしくは入出力のスキーマエラーが発生した時。
     */
    executeCallback: async <
      T extends SchemaDefinition,
      U extends keyof T['schema']['shape'],
      V extends NonStrict<ReturnType<ZodUnwrap<T['schema']['shape'][U]>['parameters']>['_input']>, // パラメタからは余剰プロパティチェック外す。
    >(
      callbackSchemaDefinition: T,
      callback: U,
      params: V,
    ): Promise<Awaited<ReturnType<ZodUnwrap<T['schema']['shape'][U]>['returnType']>['_output']>> => {
      const messageId = issueNumber();
      // executeCallbackは排他実行。
      const ok = await concurrentManager.start();

      const funcSchema = zodUnwrap(
        callbackSchemaDefinition.schema.shape[callback as keyof typeof callbackSchemaDefinition.schema.shape],
      );
      const returnTypeSchema = zodUnwrap(funcSchema.returnType());
      const parametersSchema = funcSchema.parameters();

      const callbackUnlistenList: (() => void)[] = [];
      const unlistenAll = () => {
        callbackUnlistenList.splice(0).forEach((v) => v());
      };

      try {
        if (!clientCallbackPort) {
          await connectionCompletePromise;
        }
        if (!clientCallbackPort) throw new Error('接続不良');
        const currentClientCallbackPort = clientCallbackPort;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return await new Promise<any>((resolve, reject) => {
          const onMessage = (ev: MessageEvent) => {
            // console.log('onMessage: ');
            // console.log(ev.data);
            const parseEventMessageResult = callbackResponseDataSchema.safeParse(ev.data);
            // 関係なさそうなメッセージは捨てる。
            if (!parseEventMessageResult.success || parseEventMessageResult.data.messageId !== messageId) return;
            if (parseEventMessageResult.data.success) {
              // 成功。システム境界を超えてるので不正入力チェックとしてここで再度バリデーションを行うよ。ModalDriver側でもやってるけどね。
              const parseReturnValueResult = returnTypeSchema.safeParse(parseEventMessageResult.data.payload);
              if (parseReturnValueResult.success) {
                resolve(parseReturnValueResult.data);
              } else {
                // 受け入れデータがバリデーションエラーとなった場合（ModalDriver以外が値を部混んできた場合ありうる。）
                const message = zodErrorToMessage(parseReturnValueResult.error);
                const { stack, name } = parseReturnValueResult.error;
                const error = new Error(message);
                error.stack = stack;
                error.name = name;
                reject(error);
              }
            } else {
              // エラーが連携された場合。
              const { name, message, stack } = parseEventMessageResult.data.error;
              const error = new Error(message);
              error.stack = stack;
              error.name = name;
              reject(error);
            }
          };
          // コールバックの応答にタイムアウトをさずける
          // TODO: 30秒に指定してるけどタイムアウトについて真面目に考える。ハンドリングとか
          const timer = window.setTimeout(() => {
            reject(new Error(`コールバック [${String(callback)}] が応答しません。`));
          }, 30000);
          callbackUnlistenList.push(() => {
            window.clearTimeout(timer);
          });

          // コールバックレスポンスメッセージのイベント購読を開始する。
          // console.log('購読開始？');
          currentClientCallbackPort.addEventListener('message', onMessage);
          callbackUnlistenList.push(() => {
            // console.log('購読を終了');
            currentClientCallbackPort.removeEventListener('message', onMessage);
          });
          currentClientCallbackPort.start();

          // コールバックリクエストメッセージを発射。
          // console.log('コールバックを要求');
          // console.log({
          //   messageId,
          //   type: callback,
          //   payload: parametersSchema.parse(params),
          // });
          currentClientCallbackPort.postMessage({
            messageId,
            type: callback,
            payload: parametersSchema.parse(params),
            service: callbackSchemaDefinition.service,
          });
        });
      } catch (e) {
        console.error('error');
        console.error(e);
        concurrentManager.reject(e);
        throw e;
      } finally {
        unlistenAll();
        ok();
      }
    },
    /**
     * clientから送信されてくるコマンドリクエストに対するイベントリスなを登録します。
     *
     * コンポーネントライフサイクルが終わるタイミングでリスナーが解除されるため、リスナー解除は任意です。
     *
     * @param listener
     * @returns イベント購読を解除するためのコールバック。
     */
    addCommandMessageEventListener: (listener: (ev: MessageEvent) => unknown): (() => void) => {
      // ライフサイクル終了後している場合、リスナー登録しない。
      if (unmounted) return () => {};

      // コネクション確立済の場合。
      if (commandReceiverPort) {
        const currentCommandReceiverPort = commandReceiverPort;
        currentCommandReceiverPort.addEventListener('message', listener);
        const unlisten = () => {
          currentCommandReceiverPort.removeEventListener('message', listener);
          unlistenList = unlistenList.filter((v) => v !== unlisten);
        };
        unlistenList.push(unlisten);
        // 初めてリスナー登録する時はここで受信開始。
        currentCommandReceiverPort.start();
        return unlisten;
      }

      // 以下コネクション未確立の場合。
      {
        let unListened = false;
        let unListen = () => {};
        const unlistenProxy = () => {
          unListened = true;
          unListen();
        };
        void (async () => {
          // コネクションされるのを待つ。
          await connectionCompletePromise;
          // コネクトしてまだunlistenされてなければその時、リスナー登録を実行する。
          if (!unListened) {
            unListen = con.addCommandMessageEventListener(listener);
          }
        })();
        return unlistenProxy;
      }
    },
  };
  return con;
};
export default useClientConnection;

export const connectClient = () => {
  const originRef = ref<string>('');
  // 任意に書き換えできないようにして・・・
  const firstMessageHandler = (event: MessageEvent<{ type: string; payload?: unknown }>) => {
    if (event.data.type === 'BOOT' && event?.ports?.[0] instanceof MessagePort) {
      // 有効なメッセージを処理したら、メッセージの受付を終える。
      // window.postMessageによるメッセージは起動直後1発きりで、以降はmessageChannel経由でデータをやり取りする。
      window.removeEventListener('message', firstMessageHandler);

      // APIへのリクエストに使用するoriginを保持しておく。
      originRef.value = event.origin;

      // 通信のためのMessagePort
      [commandReceiverPort, clientCallbackPort] = event.ports;

      // ModalDriver側に接続できたことを知らせる。
      // `port#start()` しないでも送信できる。受信チャネルとして使う場合は `port#start()` する必要あるけど。
      commandReceiverPort.postMessage(null);

      // コネクション確立完了。
      connectionResolve();
    }
  };
  window.addEventListener('message', firstMessageHandler);
  return computed(() => originRef.value);
};

export const ORIGIN_INJECTION_KEY = Symbol('origin');

export const useProvideOrigin = (originRef: Ref<string>) => {
  provide(ORIGIN_INJECTION_KEY, originRef);
};

export const useOrigin = () => inject<Ref<string>>(ORIGIN_INJECTION_KEY)!;
